import React, { ComponentType, useCallback, useMemo } from "react";
import { Platform } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { Spinner } from "@lookiero/aurora";
import { OnI18nErrorFn } from "@lookiero/i18n-react";
import { MessagingRoot } from "@lookiero/messaging-react/bootstrap";
import { Locale } from "@lookiero/sty-psp-locale";
import {
  sentryLoggerHOC,
  sentryLogger,
  SentryEnvironment,
  SentryLoggerFunctionArgs,
} from "@lookiero/sty-psp-logging/sentry";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { i18n } from "./i18n/i18n";
import { LookAndLike, LookAndLikeProps } from "./views/LookAndLike";

interface RootFunctionArgs {
  readonly Messaging: MessagingRoot;
  readonly development?: boolean;
  readonly sentry: () => SentryEnvironment;
}

interface RootFunction {
  (args: RootFunctionArgs): (args: SentryLoggerFunctionArgs) => ComponentType<RootProps>;
}

interface RootProps extends LookAndLikeProps {
  readonly locale?: Locale;
  readonly tradename: Tradename;
}

const root: RootFunction = ({ Messaging, development, sentry }) => {
  const logger = sentryLogger(sentry);

  const Root = ({ locale = Locale.en_GB, tradename, ...props }: RootProps) => {
    const handleOnI18nError = useCallback<OnI18nErrorFn>(
      (error, fingerprint) =>
        logger.captureException(error, fingerprint ? { captureContext: { fingerprint: [...fingerprint] } } : undefined),
      [],
    );

    const I18nComponent = useMemo(() => i18n({ tradename }), [tradename]);

    return (
      <Messaging includeReactQueryDevTools={Platform.OS === "web"}>
        <I18nComponent loader={<Spinner />} locale={locale} onError={development ? undefined : handleOnI18nError}>
          <SafeAreaProvider>
            <LookAndLike {...props} />
          </SafeAreaProvider>
        </I18nComponent>
      </Messaging>
    );
  };

  const hoc = sentryLoggerHOC({ logger });

  /**
   * It's important to not call sentryLoggerHOC each time this function gets called.
   * That's why its called outside this method.
   */
  return ({ customerId }) => hoc({ customerId })(Root);
};

export type { RootProps };
export { root };
